<template>
  <v-dialog fullscreen v-model="dialog" persistent max-width="700px">
    <v-card dark class="pb-4">
      <v-stepper v-model="step" class="elevation-0">
        <v-stepper-header>
          <v-stepper-step :complete="step > 1" step="1">
            Kundendaten
            <small>Informationen prüfen</small>
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 2" step="2">
            Pakete auswählen
            <small>Upgrade wählen</small>
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 3" step="3">
            Bestätigung
            <small>Unterschreiben</small>
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="4">
            Bezahlung
            <small>Abschliessen</small>
          </v-stepper-step>
        </v-stepper-header>
      </v-stepper>

      <v-card-title class="text-h5 primary white--text pt-4 pb-4">
        <v-icon dark left>mdi-star-plus-outline</v-icon>
        Zusatzpakete Upgrade
      </v-card-title>

      <v-window v-model="step" touchless>
        <v-window-item :value="1">
          <v-container class="pa-6">
            <div v-if="!customer.selection || !customer.contractCreator">
              <v-row>
                <v-col cols="12">
                  <user-search
                    :value="customer.contractCreator"
                    :disabled="viewOnly"
                    ignoreRights
                    instructor
                    label="Visum (Vertragersteller)"
                    outlined
                    @update="updateCreator"></user-search>
                </v-col>
                <v-col cols="12">
                  <user-search
                    :value="customer.selection"
                    :disabled="viewOnly || customer.contractCreator === null"
                    ignoreRights
                    label="Kunde"
                    outlined
                    @update="updateCustomer"></user-search>
                </v-col>
              </v-row>
            </div>
            <div v-else>
              <div class="text-center mb-4" v-if="!useCustomer">
                <v-btn v-if="!viewOnly && customer.infos" color="grey" variant="text" @click="resetCustomer">
                  <v-icon left>mdi-account-refresh-outline</v-icon>
                  Kunde ändern
                </v-btn>
                <v-progress-circular v-else color="primary" indeterminate></v-progress-circular>
              </div>

              <v-row v-if="customer.infos">
                <v-col cols="12">
                  <v-select
                    :disabled="viewOnly"
                    hide-details
                    label="Anrede"
                    :items="[
                      { name: 'Herr', value: 'm' },
                      { name: 'Frau', value: 'w' },
                    ]"
                    item-value="value"
                    item-text="name"
                    v-model="customer.infos.gender"
                    outlined
                    dense></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field hide-details :disabled="viewOnly" label="Vorname" v-model="customer.infos.firstName" outlined dense></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field hide-details :disabled="viewOnly" label="Nachname" v-model="customer.infos.lastName" outlined dense></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    hide-details
                    :disabled="viewOnly"
                    label="Geburtstag"
                    type="date"
                    v-model="customer.infos.birthday"
                    outlined
                    dense></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field hide-details :disabled="viewOnly" label="Telefon" v-model="customer.infos.phone" outlined dense></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    hide-details
                    :disabled="viewOnly"
                    label="E-Mail"
                    type="email"
                    v-model="customer.infos.email"
                    outlined
                    dense></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field hide-details :disabled="viewOnly" label="Strasse" v-model="customer.infos.street" outlined dense></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    hide-details
                    :disabled="viewOnly"
                    type="number"
                    label="Postleitzahl"
                    v-model="customer.infos.zip"
                    outlined
                    dense></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field hide-details :disabled="viewOnly" label="Stadt" v-model="customer.infos.city" outlined dense></v-text-field>
                </v-col>
              </v-row>
            </div>
          </v-container>
        </v-window-item>

        <v-window-item :value="2">
          <v-container class="pa-6">
            <v-alert v-if="isBlackFriday" type="info" class="black-friday-banner mb-6" dark>
              <v-row align="center" no-gutters>
                <v-col class="grow">
                  <div class="text-h5 font-weight-bold">BLACK FRIDAY</div>
                  <div>Nur für kurze Zeit: 20% Rabatt auf alle Upgrades!</div>
                </v-col>
                <v-col cols="auto">
                  <v-icon size="64">mdi-sale</v-icon>
                </v-col>
              </v-row>
            </v-alert>

            <v-row v-if="fetchingPossibilities">
              <v-col cols="12" class="text-center pa-10">
                <v-progress-circular size="64" color="primary" indeterminate></v-progress-circular>
                <p class="mt-4 text-grey">Lade verfügbare Pakete...</p>
              </v-col>
            </v-row>
            <v-row v-else-if="possibilities">
              <v-col cols="12">
                <v-alert v-if="!showExternalPackages" outlined color="primary" border="left" type="info">
                  {{ possibilities.roodyMembershipName }} bis {{ $moment(possibilities.endDate).format("DD.MM.YYYY") }}
                </v-alert>
                <v-alert v-else type="info" color="primary">Zusatzpakete aus anderen Studios</v-alert>
              </v-col>

              <v-col cols="12">
                <v-row>
                  <v-col cols="12" md="6" v-for="(pck, index) in packages.filter((e) => true)" :key="index">
                    <v-card
                      class="package-card fill-height d-flex align-center"
                      :class="{ 'package-card--selected': selectedPackages.includes(pck), 'package-card--bf': isBlackFriday }"
                      elevation="2"
                      @click="togglePackage(pck)">
                      <v-icon class="package-card__check-icon" color="white">mdi-check-circle</v-icon>
                      <div class="black-friday-chip" v-if="isBlackFriday">
                        <v-icon small left color="#ffd700">mdi-sale</v-icon>
                        BLACK FRIDAY
                      </div>
                      <div class="special-offer-chip" v-if="pck.packageName.toLowerCase().includes('pelvi') && !isBlackFriday">NEU</div>

                      <v-card-text>
                        <v-row align="center" no-gutters>
                          <v-col>
                            <div class="text-h6 package-title">
                              <span v-if="pck.amountSelectable && pck.amount">{{ pck.amount }}x </span>
                              {{ pck.packageName }}
                            </div>

                            <div class="package-subtitle" v-if="pck.amountSelectable && pck.amount">
                              <div v-if="isBlackFriday">
                                <span class="bf-price-new">
                                  {{ displayCurrency(pck.amount * parseInt(pck.price) * 0.8) }}
                                </span>
                                <span class="text-decoration-line-through ml-2 bf-price-old">
                                  {{ displayCurrency(pck.amount * parseInt(pck.price)) }}
                                </span>
                              </div>
                              <div v-else>
                                {{ displayCurrency(pck.amount * parseInt(pck.price)) }}
                                <span
                                  class="text-decoration-line-through ml-2"
                                  v-if="
                                    pck.usualPrice &&
                                    (pck.calculateWithEndDate ||
                                      (pck.calculateWithEndDateSixMonths && parseInt(pck.price) !== parseInt(pck.usualPrice)))
                                  ">
                                  {{ displayCurrency(pck.amount * pck.usualPrice) }}
                                </span>
                              </div>
                            </div>

                            <div class="package-subtitle" v-else>
                              <div v-if="isBlackFriday">
                                <span class="bf-price-new">
                                  <span v-if="isPelviAnteilPackage(pck)">
                                    {{ displayCurrency(calcPelvixPrice(pck, true)) }} (laufendes Vertragsjahr)
                                  </span>
                                  <span v-else>
                                    {{ displayCurrency(pck.price * 0.8) }}
                                  </span>
                                </span>
                                <span class="text-decoration-line-through ml-2 bf-price-old">
                                  <span v-if="isPelviAnteilPackage(pck)">
                                    {{ displayCurrency(calcPelvixPrice(pck, false)) }}
                                  </span>
                                  <span v-else>
                                    {{ displayCurrency(pck.price) }}
                                  </span>
                                </span>
                              </div>

                              <div v-else>
                                <span v-if="pck.packageName.toLowerCase().includes('pelvi') && pck.packageName.toLowerCase().includes('anteil')">
                                  {{ displayCurrency(calcPelvixPrice(pck)) }} (laufendes Vertragsjahr)</span
                                >
                                <span v-else> {{ displayCurrency(pck.price) }}</span>

                                <span
                                  class="text-decoration-line-through ml-2"
                                  v-if="
                                    pck.usualPrice &&
                                    (pck.calculateWithEndDate ||
                                      (pck.calculateWithEndDateSixMonths && parseInt(pck.price) !== parseInt(pck.usualPrice)))
                                  ">
                                  <span v-if="pck.packageName.toLowerCase().includes('pelvi') && pck.packageName.toLowerCase().includes('anteil')">
                                    {{ displayCurrency(pck.usualPriceYear) }} pro Jahr</span
                                  >
                                  <span v-else> {{ displayCurrency(pck.usualPrice) }}</span>
                                </span>
                              </div>
                            </div>
                          </v-col>
                          <v-col cols="auto">
                            <v-tooltip bottom v-if="showExternalPackages && pck.availableInStudios">
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon v-on="on" v-bind="attrs" @click.stop>
                                  <v-icon>mdi-map-marker-outline</v-icon>
                                </v-btn>
                              </template>
                              <span>{{
                                pck.availableInStudios
                                  .filter((e) =>
                                    possibilities.studio === "5f3d48195dd7955ad48de5e3" && pck.packageName.toLowerCase().includes("pelvi")
                                      ? e !== "Winterthur"
                                      : true
                                  )
                                  .join(", ")
                              }}</span>
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>

              <v-col v-if="possibilities.packages && possibilities.packages.length > 0" cols="12">
                <v-alert type="success" icon="mdi-layers-triple-outline">
                  <b>Vorhandene Zusatzpakete:</b> {{ possibilities.packages.join(", ") }}
                </v-alert>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12">
                <v-alert type="error" border="start">
                  Keine Mitgliedschaft oder keine verfügbaren Zusatzpakete gefunden. Stimmt die E-Mail im TAC überein?
                </v-alert>
              </v-col>
            </v-row>
          </v-container>
        </v-window-item>

        <v-window-item :value="3">
          <v-container class="pa-6">
            <upgrade-summary
              :selected-packages="selectedPackages"
              :total-price="totalPackagePrice"
              v-if="!checkbox"
              :is-black-friday="isBlackFriday"
              :original-total-price="originalTotalPackagePrice"
              :total-discount="totalDiscountAmount"
              :left-training-years="leftTrainingYears">
            </upgrade-summary>
            <v-alert v-if="salePackages.length > 0 && !checkbox && isBlackFriday" class="mt-5 mx-auto" type="info" color="black" dark>
              <v-icon left>mdi-sale</v-icon>
              <b>Black Friday Deal:</b>
              {{ salePackages.length === 1 ? "Dein Paket" : "Deine Pakete" }}
              <b>{{ salePackages.map((e) => e.packageName).join(", ") }}</b>
              {{ salePackages.length === 1 ? "erneuert" : "erneuern" }} sich zum Regulärpreis von
              <b
                >{{
                  displayCurrency(
                    salePackages.reduce((acc, e) => acc + (e.amountSelectable && e.amount ? e.amount * e.usualPriceYear : e.usualPriceYear), 0)
                  )
                }}
                pro Jahr</b
              >.
            </v-alert>

            <v-alert v-if="salePackages.length > 0 && !checkbox && !isBlackFriday" class="mt-5 mx-auto" type="info">
              {{ salePackages.length === 1 ? "Das Aktionspaket" : "Die Aktionspakete" }}
              <b>{{ salePackages.map((e) => e.packageName).join(", ") }}</b>
              {{ salePackages.length === 1 ? "erneuert" : "erneuern" }} sich zum Regulärpreis von
              <b
                >{{
                  displayCurrency(
                    salePackages.reduce((acc, e) => acc + (e.amountSelectable && e.amount ? e.amount * e.usualPriceYear : e.usualPriceYear), 0)
                  )
                }}
                pro Jahr</b
              >.
            </v-alert>

            <v-alert v-if="pelviAnteilPackage && !checkbox" class="mt-5 mx-auto" outlined icon="mdi-information-outline">
              <div class="text-h6 mb-2">Kostenaufstellung für Pelvixpower-Stuhl</div>
              <p>
                Für das laufende Vertragsjahr bezahlst du nur den anteilsmässigen Betrag von
                <b>{{ displayCurrency(calcPelvixPrice(pelviAnteilPackage)) }}</b
                >.
              </p>
              <p class="mb-0">
                <span v-if="leftTrainingYears === 0">Im Folgejahr</span><span v-else>In den Folgejahren</span> wird der reguläre Jahrespreis von
                <b>{{ displayCurrency(pelviAnteilPackage.usualPriceYear) }}</b> fällig.
              </p>

              <div v-if="possibilities.duration.years > possibilities.trainingDurationInYears + 1" class="mt-2">
                <b>Zukünftige jährliche Kosten:</b>
                <ul class="pl-5 mt-2">
                  <li v-for="yearOffset in possibilities.duration.years - (possibilities.trainingDurationInYears + 1)" :key="yearOffset">
                    Jahr {{ possibilities.trainingDurationInYears + 1 + yearOffset }}: <b>{{ displayCurrency(pelviAnteilPackage.usualPriceYear) }}</b>
                  </li>
                </ul>
              </div>
              <v-divider class="my-3"></v-divider>
              <p class="mb-0 font-weight-medium">
                <v-icon small left>mdi-file-sign</v-icon>
                Dieses Zusatzpaket ist an die Laufzeit deiner Hauptmitgliedschaft gebunden und kann nicht separat gekündigt werden.
              </p>
            </v-alert>

            <v-alert class="mt-5" icon="mdi-gavel" type="warning" outlined v-if="!checkbox">
              Dieser Vertrag verlängert sich automatisch um jeweils 12 Monate, wenn dieser nicht 45 Tage vor Vertragsablauf schriftlich gekündigt
              wird. Massgebend für das Einhalten der Frist ist das Datum des Poststempels. Der Mitgliedschaftspreis für den verlängerten Vertrag
              richtet sich nach der, 60 Tage vor Verlängerungsbeginn, geltenden Mitgliedschaftspreisliste (die Preisliste liegt immer in jedem Center
              der well come FIT zur Einsicht auf).
            </v-alert>

            <v-row class="mt-6">
              <v-col class="justify-center d-flex" cols="12">
                <v-checkbox v-model="checkbox">
                  <template v-slot:label>
                    <div style="max-width: 500px; text-align: left">
                      Ich bestätige hiermit, dass ich meinen aktuellen Mitgliedschaftsvertrag erfolgreich mit den ausgewählten Paketen aktualisiert
                      habe und dem Upgrade zustimme.
                    </div>
                  </template>
                </v-checkbox>
              </v-col>

              <div v-if="checkbox" class="w-100">
                <v-col align="center" cols="12">
                  <v-card-subtitle class="text-h6">Unterschrift Kunde</v-card-subtitle>
                  <signature-pad
                    :signature-data="signature"
                    @startDrawing="signatureFound = true"
                    @resetDrawing="signatureFound = false"
                    :whiteBg="true"
                    max-width="400px"
                    @saveSignature="saveSignature"
                    ref="signatureConditions"></signature-pad>
                </v-col>
                <v-col v-if="customer.isUnderage" class="px-5 mt-6" cols="12">
                  <v-divider class="mb-6"></v-divider>
                  <v-card-subtitle class="text-h6 text-center">Gesetzlicher Vertreter</v-card-subtitle>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field hide-details v-model="customer.legalRepresentative.firstName" label="Vorname" outlined dense></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field hide-details v-model="customer.legalRepresentative.lastName" label="Nachname" outlined dense></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field hide-details v-model="customer.legalRepresentative.email" label="E-Mail" outlined dense></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field hide-details v-model="customer.legalRepresentative.address" label="Adresse" outlined dense></v-text-field>
                    </v-col>
                  </v-row>
                  <signature-pad
                    class="mt-4"
                    :whiteBg="true"
                    max-width="400px"
                    :signature-data="signatureLegalRepresentative"
                    @saveSignature="saveSignatureLR"
                    ref="signatureLRConditions"></signature-pad>
                </v-col>
              </div>
            </v-row>
          </v-container>
        </v-window-item>

        <v-window-item :value="4">
          <v-container class="pa-6">
            <upgrade-summary
              :selected-packages="selectedPackages"
              :total-price="totalPackagePrice"
              :is-black-friday="isBlackFriday"
              :original-total-price="originalTotalPackagePrice"
              :total-discount="totalDiscountAmount"
              :left-training-years="leftTrainingYears">
            </upgrade-summary>
            <v-alert v-if="salePackages.length > 0 && isBlackFriday" class="mt-5 mx-auto" max-width="500px" type="info" color="black" dark>
              <v-icon left>mdi-sale</v-icon>
              <b>Black Friday Deal:</b>
              {{ salePackages.length === 1 ? "Dein Paket" : "Deine Pakete" }}
              <b>{{ salePackages.map((e) => e.packageName).join(", ") }}</b>
              {{ salePackages.length === 1 ? "erneuert" : "erneuern" }} sich zum Regulärpreis von
              <b
                >{{
                  displayCurrency(
                    salePackages.reduce((acc, e) => acc + (e.amountSelectable && e.amount ? e.amount * e.usualPriceYear : e.usualPriceYear), 0)
                  )
                }}
                pro Jahr</b
              >.
            </v-alert>

            <v-alert v-if="salePackages.length > 0 && !isBlackFriday" class="mt-5 mx-auto" max-width="500px" type="info">
              {{ salePackages.length === 1 ? "Das Aktionspaket" : "Die Aktionspakete" }}
              <b>{{ salePackages.map((e) => e.packageName).join(", ") }}</b>
              {{ salePackages.length === 1 ? "erneuert" : "erneuern" }} sich zum Regulärpreis von
              <b
                >{{
                  displayCurrency(
                    salePackages.reduce((acc, e) => acc + (e.amountSelectable && e.amount ? e.amount * e.usualPriceYear : e.usualPriceYear), 0)
                  )
                }}
                pro Jahr</b
              >.
            </v-alert>

            <v-card-text class="text-center text-h6 mt-6">Wie möchtest Du bezahlen?</v-card-text>

            <div v-if="!useCustomer">
              <v-row justify="center">
                <v-col cols="auto">
                  <v-card
                    link
                    class="payment-card"
                    :class="{ 'payment-card--selected': payment === 'Kartenzahlung' }"
                    @click="payment = 'Kartenzahlung'">
                    <v-card-text class="text-center">
                      <v-icon size="48">mdi-credit-card-outline</v-icon>
                      <div class="mt-2 font-weight-bold">Kartenzahlung</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="auto">
                  <v-card link class="payment-card" :class="{ 'payment-card--selected': payment === 'Bar' }" @click="payment = 'Bar'">
                    <v-card-text class="text-center">
                      <v-icon size="48">mdi-cash-multiple</v-icon>
                      <div class="mt-2 font-weight-bold">Bar</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
              <v-row justify="center" class="mt-4">
                <v-col cols="12" md="8">
                  <v-textarea label="Notiz (optional)" v-model="paymentComment" outlined rows="2"></v-textarea>
                </v-col>
              </v-row>
            </div>
            <div v-else>
              <v-row justify="center">
                <v-col cols="auto">
                  <v-card link class="payment-card" :class="{ 'payment-card--selected': payment === 'Rechnung' }" @click="payment = 'Rechnung'">
                    <v-card-text class="text-center">
                      <v-icon size="48">mdi-receipt-text-outline</v-icon>
                      <div class="mt-2 font-weight-bold">Rechnung</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </v-container>
        </v-window-item>
      </v-window>

      <v-card-actions class="pa-4 justify-center">
        <v-btn size="large" @click="step === 1 ? cancel() : preStep()">
          {{ step === 1 ? "Abbrechen" : "Zurück" }}
        </v-btn>

        <v-btn
          size="large"
          color="primary"
          variant="flat"
          :disabled="
            !customer.selection ||
            !customer.contractCreator ||
            (customer.selection && !customer.infos) ||
            fetchingPossibilities ||
            (step === 2 &&
              selectedPackages.length === 0 &&
              !(possibilities.externalPackages && possibilities.externalPackages.length > 0 && !showExternalPackages)) ||
            (step === 3 && !checkbox) ||
            (step === 4 && !payment)
          "
          :loading="confirming || updatingUserData"
          @click="step === 1 ? updateUserData() : step !== 4 ? nextStep() : confirm()">
          {{ step !== 4 ? "Weiter" : "Bestätigen & Abschliessen" }}
          <v-icon right>{{ step !== 4 ? "mdi-arrow-right" : "mdi-check" }}</v-icon>
        </v-btn>
      </v-card-actions>

      <v-dialog persistent max-width="300px" v-model="showPackageAmountSelect">
        <v-card v-if="packageAmountSelect">
          <v-card-title class="white--text primary">
            <span class="mx-auto">Anzahl {{ packageAmountSelect.packageName }}</span>
          </v-card-title>
          <v-card-text align="center" class="pt-4">
            <span>Wähle die Anzahl aus</span>
            <v-row class="mt-5" align="center" justify="center">
              <v-btn color="red" class="white--text" rounded-xl @click="decrement">
                <v-icon>mdi-minus</v-icon>
              </v-btn>
              <span style="font-size: 16px; font-weight: bold" class="mx-4">
                {{ packageAmountSelect.amount }}
              </span>
              <v-btn color="primary" rounded-xl @click="increment">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-btn class="mx-auto" text @click="setPackageAmount">Bestätigen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>

    <v-dialog v-model="pelviAnteilPackageDialog" max-width="500px" persistent>
      <v-card dark v-if="pelviAnteilPackage">
        <v-card-title class="text-h5 primary">
          <v-icon left color="white">mdi-information-outline</v-icon>
          Pelvixpower-Stuhl Upgrade
        </v-card-title>
        <div class="ma-5">
          <div class="ma-5">
            <div class="text-h6 mb-3">Kostenaufstellung: Pelvixpower-Stuhl</div>

            <div class="mb-3">
              <div class="font-weight-bold">Heute fällig (anteiliger Preis):</div>
              Du bezahlst für das laufende Vertragsjahr nur den Betrag von <b>{{ displayCurrency(calcPelvixPrice(pelviAnteilPackage)) }}</b
              >.
            </div>

            <div class="mb-3">
              <div class="font-weight-bold">Zukünftige Kosten:</div>
              Ab dem nächsten Vertragsjahr verlängert sich das Paket jährlich zum regulären Preis von
              <b>{{ displayCurrency(pelviAnteilPackage.usualPriceYear) }} pro Jahr</b>.
            </div>

            <v-divider class="my-3"></v-divider>
            <div>
              <p class="mb-0 font-weight-medium">
                <v-icon small left>mdi-file-sign</v-icon>
                Dieses Zusatzpaket ist an die Laufzeit deiner Hauptmitgliedschaft gebunden und kann nicht separat gekündigt werden.
              </p>
            </div>
          </div>
        </div>
        <v-card-actions class="mt-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="pelviAnteilPackageDialog = false"> Verstanden </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="infoDialog" max-width="500px" persistent>
      <v-card dark>
        <v-card-title class="text-h5">
          <v-icon left color="primary">mdi-information-outline</v-icon>
          {{ infoDialogTitle }}
        </v-card-title>
        <v-card-text class="pt-4 white--text" style="font-size: 1rem; line-height: 1.6">
          {{ infoDialogText }}
        </v-card-text>
        <v-alert class="mx-5" v-if="infoDialogAlertText && infoDialogAlertText.length > 2" color="primary" border="left">
          {{ infoDialogAlertText }}
        </v-alert>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="resetInfoDialog()"> Verstanden </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import userSearch from "@/components/userSearch.vue"
import userService from "@/services/userService.js"
import { mapGetters } from "vuex"
import signaturePad from "@/components/signaturePad/signaturePad.vue"
import { roundToNearestFiveCents, roundToNearestFiveCentsWithoutFormatting } from "@/moduls/currency/currency.js"
// *** HIER GEÄNDERT ***
// import upgradeContractPackageTable from "./upgradeContractPackageTable.vue" // ENTFERNT
import upgradeSummary from "./upgradeSummary.vue" // NEU HINZUGEFÜGT
import api from "@/api"
export default {
  // *** HIER GEÄNDERT ***
  components: { userSearch, signaturePad, upgradeSummary }, // UpgradeSummary statt upgradeContractPackageTable
  props: {
    dialog: {
      default: false,
      required: true,
    },
    selectedCustomer: {
      default: null,
      required: false,
    },
    useCustomer: {
      default: false,
      required: true,
    },
  },
  data() {
    return {
      userService,
      pelviAnteilPackageDialog: false,
      infoDialog: false,
      infoDialogTitle: "",
      infoDialogText: "",
      infoDialogAlertText: null,
      updatingUserData: false,
      showExternalPackages: false,
      confirming: false,
      payment: null,
      signature: null,
      signatureLegalRepresentative: null,
      signatureFound: false,
      checkbox: false,
      packageAmountSelect: null,
      selectedPackages: [],
      showPossibilities: false,
      possibilities: null,
      fetchingPossibilities: false,
      viewOnly: false,
      paymentComment: "",
      step: 1,
      customer1: {
        isUnderage: false,
        contractCreator: "5f55fdad6716c132adf93baa",
        contractCreatorInfos: {
          firstName: "Dom",
          lastName: "Ammann",
          email: "dominic.ammann.ch@gmail.com",
        },
        selection: "63a98151c8c2993f5c6a6f72",
        legalRepresentative: {
          firstName: null,
          lastName: null,
          email: null,
          address: null,
          firstSignature: null,
          secondSignature: null,
        },
        infos: {
          gender: "m",
          firstName: "Marcel",
          lastName: "Ammann",
          email: "marcel.ammann@axacom.ch",
          phone: "+41763852899",
          birthday: "1999-12-28",
          street: "Hauptstrasse 138",
          city: "Tägerwilen",
          zip: "8274",
        },
      },
      customer: {
        isUnderage: false,
        contractCreator: null,
        contractCreatorInfos: null,
        selection: null,
        legalRepresentative: null,
        infos: {
          gender: null,
          firstName: null,
          lastName: null,
          birthday: null,
          phone: null,
          email: null,
          street: null,
          zip: null,
          city: null,
        },
      },
    }
  },
  async created() {
    if (this.getSelectedEmployee?.user) {
      this.updateCreator(this.getSelectedEmployee?.user)
    }
    if (this.selectedCustomer) {
      this.updateCustomer(this.selectedCustomer)
    }
    this.initCustomer()
  },
  watch: {},
  computed: {
    ...mapGetters("studioStore", ["getSelectedEmployee"]),

    isBlackFriday() {
      const today = new Date()
      const year = today.getFullYear()
      // Monate sind 0-indiziert (0 = Jan, 10 = Nov)
      const startDate = new Date(year, 10, 19) // 19. November
      const endDate = new Date(year, 11, 7) // 7. Dezember
      endDate.setHours(23, 59, 59, 999) // Inklusive dem 7.12.

      // --- WICHTIG: FÜR TESTS ---
      // Aktuelles Datum simulieren, um die Anzeige zu testen
      // const today = new Date("2025-11-20") // Ein Datum INNERHALB des BF-Zeitraums
      // const today = new Date("2025-11-01") // Ein Datum AUSSERHALB des BF-Zeitraums

      // Im echten Betrieb die Zeile unten auskommentieren und die simulierte Zeile einkommentieren
      return (today >= startDate && today <= endDate) || this.userService.hasRight("c_showBlackFridayOffer")
      // return true // Permanent für Tests
      // --- ENDE TESTS ---
    },

    leftTrainingYears() {
      return this.possibilities && this.possibilities.duration
        ? this.possibilities.duration.years - (this.possibilities.trainingDurationInYears + 1)
        : 0
    },
    pelviAnteilPackage() {
      return this.selectedPackages.find((pck) => pck.packageName.toLowerCase().includes("pelvi") && pck.packageName.toLowerCase().includes("anteil"))
    },

    fullContractValue() {
      let total = this.selectedPackages
        .filter((p) => {
          if (!this.pelviAnteilPackage) {
            return true
          } else {
            return !this.isPelviAnteilPackage(p)
          }
        })
        .reduce((acc, p) => {
          const amount = p.amountSelectable && p.amount ? p.amount : 1
          let packagePrice = parseFloat(p.price)

          if (this.isBlackFriday) {
            packagePrice = packagePrice * 0.8
          }

          return acc + packagePrice * amount
        }, 0)

      if (this.pelviAnteilPackage && this.possibilities) {
        const currentYearPrice = this.calcPelvixPrice(this.pelviAnteilPackage)
        const futureYearsPrice =
          (this.possibilities.duration.years - (this.possibilities.trainingDurationInYears + 1)) * this.pelviAnteilPackage.usualPriceYear

        total += currentYearPrice + futureYearsPrice
      }

      return total
    },

    pelviAnteilActivated() {
      return !!this.pelviAnteilPackage
    },
    salePackages() {
      return this.selectedPackages.filter((s) => {
        const isRegularSale =
          s.usualPrice && (s.calculateWithEndDate || (s.calculateWithEndDateSixMonths && parseInt(s.price) !== parseInt(s.usualPrice)))
        const isBlackFridaySale = this.isBlackFriday && (s.usualPriceYear || s.usualPrice)
        return isRegularSale || isBlackFridaySale
      })
    },
    packages() {
      let pack = []
      if (this.showExternalPackages) {
        if (!this.possibilities) return pack
        pack = this.possibilities.externalPackages || []
      } else {
        if (!this.possibilities) return pack
        pack = this.possibilities.availablePackages || []
      }
      if (this.customer.isUnderage) pack = pack.filter((e) => !e.hideForMinors)
      return pack
    },
    studio() {
      return this.possibilities.studioPopulated || { code: null, name: null }
    },

    totalPackagePrice() {
      let price = 0
      this.selectedPackages.forEach((e) => {
        let priceLocal = 0
        if (e.packageName.toLowerCase().includes("pelvi") && e.packageName.toLowerCase().includes("anteil")) {
          priceLocal = this.calcPelvixPrice(e)
        } else {
          let amount = e.amountSelectable && e.amount ? e.amount : 1
          priceLocal = parseFloat(e.price) * amount

          if (this.isBlackFriday) {
            priceLocal = priceLocal * 0.8
          }
        }
        price += roundToNearestFiveCentsWithoutFormatting(priceLocal)
      })
      return price
    },

    originalTotalPackagePrice() {
      if (!this.isBlackFriday) return 0

      let price = 0
      this.selectedPackages.forEach((e) => {
        let priceLocal = 0
        if (this.isPelviAnteilPackage(e)) {
          priceLocal = this.calcPelvixPrice(e, false) // 'false' = Rabatt ignorieren
        } else {
          let amount = e.amountSelectable && e.amount ? e.amount : 1
          priceLocal = parseFloat(e.price) * amount
        }
        price += roundToNearestFiveCentsWithoutFormatting(priceLocal)
      })
      return price
    },

    totalDiscountAmount() {
      if (!this.isBlackFriday) return 0
      const discount = this.originalTotalPackagePrice - this.totalPackagePrice
      return roundToNearestFiveCentsWithoutFormatting(discount)
    },

    showPackageAmountSelect() {
      return this.packageAmountSelect !== null
    },
  },
  methods: {
    resetInfoDialog() {
      this.infoDialog = false
      this.infoDialogAlertText = null
      this.infoDialogTitle = ""
      this.infoDialogText = ""
    },
    isPelviAnteilPackage(pck) {
      if (!pck || !pck.packageName) return false
      return pck.packageName.toLowerCase().includes("pelvi") && pck.packageName.toLowerCase().includes("anteil")
    },
    calcPelvixPrice(pck, applyDiscount = true) {
      let price = pck.price - this.leftTrainingYears * pck.usualPriceYear

      if (this.isBlackFriday && applyDiscount) {
        price = price * 0.8
      }
      return price
    },
    async initCustomer() {
      if (this.useCustomer) {
        this.payment = "Rechnung"
        let user = await api.getUser()
        if (user && user.user) {
          let userInfos = user.user
          this.customer.contractCreator = userInfos._id
          let contractCreatorInfos = {
            firstName: userInfos.vorname,
            lastName: userInfos.nachname,
            email: userInfos.email,
          }

          this.customer.contractCreatorInfos = contractCreatorInfos

          this.customer.selection = userInfos._id

          let customerEntry = {
            gender: userInfos.geschlecht,
            firstName: userInfos.vorname,
            lastName: userInfos.nachname,
            email: userInfos.email,
            phone: userInfos.telefon,
            birthday: userInfos.geburtstag,
            street: userInfos.adresse_1,
            city: userInfos.stadt,
            zip: userInfos.plz,
          }

          this.resetLegalRepresentative()

          this.customer.infos = customerEntry

          if (!this.checkIfBirthdayIsValid(this.customer.infos.birthday)) {
            this.$toast.error("Bitte gib ein gültiges Geburtsdatum ein")
          } else {
            this.checkIfUnderage()
          }
        }
      }
    },
    cancel() {
      this.updatingUserData = false
      this.$emit("hide")
      this.checkbox = false
      this.selectedPackages = []
      this.step = 1
      this.signature = null
      this.signatureLegalRepresentative = null
      this.paymentComment = ""
      this.customer = {
        isUnderage: false,
        contractCreator: null,
        contractCreatorInfos: null,
        selection: null,
        legalRepresentative: {
          firstName: null,
          lastName: null,
          email: null,
          address: null,
          firstSignature: null,
          secondSignature: null,
        },
        infos: {
          gender: null,
          firstName: null,
          lastName: null,
          birthday: null,
          phone: null,
          email: null,
          street: null,
          zip: null,
          city: null,
        },
      }
      this.initCustomer()
    },
    async confirm() {
      this.confirming = true
      let additionalEmailText = ""

      if (this.pelviAnteilPackage) {
        let futurePaymentsHtml = ""
        const austehendeJahre = this.leftTrainingYears

        if (austehendeJahre > 0) {
          const listItems = Array.from({ length: austehendeJahre }, (_, i) => {
            const yearNumber = this.possibilities.trainingDurationInYears + 1 + (i + 1)
            const price = this.displayCurrency(this.pelviAnteilPackage.usualPriceYear)
            return `<li style="margin-bottom: 5px;">Jahr ${yearNumber}: <strong>${price}</strong></li>`
          }).join("")

          futurePaymentsHtml = `
                <div style="margin-top: 10px;">
                    <strong style="font-size: 14px;">Zukünftige jährliche Kosten:</strong>
                    <ul style="padding-left: 20px; margin-top: 8px; margin-bottom: 0;">
                        ${listItems}
                    </ul>
                </div>
            `
        }

        additionalEmailText = `
            <div style="font-family: Arial, sans-serif; color: #333; border: 1px solid #e0e0e0; padding: 20px; border-radius: 8px; max-width: 600px; margin: 20px 0;">
                <h3 style="font-size: 18px; color: #000; margin-top: 0; margin-bottom: 16px;">
                    Detail-Übersicht für Ihr Zusatzpaket: Pelvixpower-Stuhl
                </h3>

                <div style="margin-bottom: 16px; font-size: 14px; line-height: 1.6;">
                    <strong style="display: block; margin-bottom: 4px;">Heute fällig (anteiliger Preis):</strong>
                    Du bezahlst für das laufende Vertragsjahr nur den Betrag von <strong>${this.displayCurrency(
                      this.calcPelvixPrice(this.pelviAnteilPackage)
                    )}</strong>.
                </div>

                <div style="margin-bottom: 16px; font-size: 14px; line-height: 1.6;">
                    <strong style="display: block; margin-bottom: 4px;">Zukünftige Kosten:</strong>
                    Ab dem nächsten Vertragsjahr verlängert sich das Paket jährlich zum regulären Preis von <strong>${this.displayCurrency(
                      this.pelviAnteilPackage.usualPriceYear
                    )} pro Jahr</strong>.
                </div>

                ${futurePaymentsHtml}

                <hr style="border: 0; border-top: 1px solid #e0e0e0; margin: 20px 0;">

                <div style="font-size: 13px; line-height: 1.6; color: #555;">
                     <strong style="color: #000;">Hinweis zur Vertragslaufzeit:</strong><br>
                    Dieses Zusatzpaket ist an die Laufzeit deiner Hauptmitgliedschaft gebunden und kann nicht separat gekündigt werden.
                </div>
            </div>
        `
      }

      let contractInfo = {
        paymentComment: this.paymentComment,
        possibilities: this.possibilities,
        studio: this.possibilities.studio,
        customer: this.customer,
        signature: this.signature,
        signatureLegalRepresentative: this.signatureLegalRepresentative,
        packages: this.selectedPackages,
        payment: this.payment,
        checkbox: this.checkbox,
        frontendPrice: this.totalPackagePrice,
        isBlackFriday: this.isBlackFriday,
      }
      try {
        let status = await api.createContractUpgrade({ contractInfo, useCustomer: this.useCustomer, additionalEmailText })
        console.log(status)
        this.$toast.success("Upgrade erstellt")
        this.cancel()
        this.$emit("created")
      } catch (e) {
        this.$toast.error("Fehler beim Erstellen, bitte versuche es erneut")
      }
      this.confirming = false
    },
    saveSignature(signature) {
      this.signature = signature
    },
    saveSignatureLR(signature) {
      this.signatureLegalRepresentative = signature
    },
    increment() {
      if (this.packageAmountSelect.amount < 10) {
        this.packageAmountSelect.amount++
      }
      this.$forceUpdate()
    },
    decrement() {
      if (this.packageAmountSelect.amount > 1) {
        this.packageAmountSelect.amount--
      }
      this.$forceUpdate()
    },
    preStep() {
      if (this.step === 2 && this.showExternalPackages) {
        this.showExternalPackages = false
        return
      }
      if (this.step !== 1) {
        this.step--
      }
    },
    nextStep() {
      if (this.step === 2) {
        this.checkIfUnderage()
      }

      if (this.step === 2 && !this.showExternalPackages && this.possibilities.externalPackages && this.possibilities.externalPackages.length > 0) {
        this.showExternalPackages = true
        return
      }
      if (this.step === 3) {
        if (!this.signatureFound) {
          this.$toast.error("Bitte unterschreibe das Upgrade")
          return
        }
        this.$refs.signatureConditions.saveSignature()
        if (this.customer.isUnderage) {
          this.$refs.signatureLRConditions.saveSignature()
        }

        const defaultBFComment = "Black Friday Rabatt (20%) gewährt."
        if (this.isBlackFriday && (this.paymentComment === "" || this.paymentComment === defaultBFComment)) {
          this.paymentComment = defaultBFComment
        } else if (!this.isBlackFriday && this.paymentComment === defaultBFComment) {
          this.paymentComment = ""
        }
      }
      this.step++
    },
    setPackageAmount() {
      this.packageAmountSelect = null
    },
    togglePackage(pck) {
      const index = this.selectedPackages.indexOf(pck)
      let pevli3YearsSkip = false

      if (
        index === -1 &&
        pck.packageName.toLowerCase().includes("anteil") &&
        pck.packageName.toLowerCase().includes("pelvi") &&
        (this.possibilities.duration?.years === 3 || this.possibilities.duration?.years === 4)
      ) {
        if (pck.price > pck.usualPriceYear) {
          if (this.leftTrainingYears > 0) {
            pevli3YearsSkip = true
          }
        }
      }
      if (
        index === -1 &&
        pck.packageName.toLowerCase().includes("anteil") &&
        pck.packageName.toLowerCase().includes("pelvi") &&
        this.useCustomer &&
        !pevli3YearsSkip
      ) {
        this.infoDialogTitle = "Nur vor Ort buchbar"
        this.infoDialogText =
          "Das Paket 'Pelvipower-Stuhl anteilsmässig' kann nur direkt bei uns im Studio gelöst werden. Bitte frage einfach einen unserer Trainer, sie helfen dir gerne weiter!"
        this.infoDialog = true
        if (this.possibilities.studio === "5f3d482d5dd7955ad48de5e4") {
          this.infoDialogAlertText = "An den Standorten Frauenfeld und Wil stehen ein Pelvipower-Stuhl für dein Training zur Verfügung."
        }
        return
      }
      if (pevli3YearsSkip && !this.pelviAnteilPackage) {
        this.pelviAnteilPackageDialog = true
      }
      if (
        index === -1 &&
        pck.packageName.toLowerCase().includes("anteil") &&
        pck.packageName.toLowerCase().includes("pelvi") &&
        !this.useCustomer &&
        !pevli3YearsSkip
      ) {
        this.infoDialogTitle = "Neuer Vertrag erforderlich"
        if (this.possibilities.studio === "5f3d482d5dd7955ad48de5e4") {
          this.infoDialogAlertText = "An den Standorten Frauenfeld und Wil stehen ein Pelvipower-Stuhl für dein Training zur Verfügung."
        }
        if (this.possibilities.roodyMembershipName.includes("premium")) {
          this.infoDialogText =
            "Kunde muss sofort einen neuen Vertrag mit PREMIUM Mitgliedschaft für die nächste Laufzeit (12 Monate) unterzeichnen (mit dem Zusatzpaket Pelvipower-Stuhl). "
        } else {
          this.infoDialogText =
            "Kunde muss sofort einen neuen Vertrag für die nächste Laufzeit (12 Monate) unterzeichnen (mit dem Zusatzpaket Pelvipower-Stuhl). "
        }

        this.infoDialog = true
      }
      if (
        index === -1 &&
        !pck.packageName.toLowerCase().includes("anteil") &&
        pck.packageName.toLowerCase().includes("pelvi") &&
        this.possibilities.studio === "5f3d482d5dd7955ad48de5e4"
      ) {
        this.infoDialogTitle = "Pelvipower-Stuhl Upgrade"
        if (this.possibilities.studio === "5f3d482d5dd7955ad48de5e4") {
          this.infoDialogAlertText = "An den Standorten Frauenfeld und Wil stehen ein Pelvipower-Stuhl für dein Training zur Verfügung."
        }

        this.infoDialog = true
      }
      this.packageAmountSelect = null

      if (index > -1) {
        if (pck.amountSelectable) pck.amount = 0
        this.selectedPackages.splice(index, 1)
        if (
          this.studio.code === "stgalleneinstein" &&
          pck.packageName === "Wellness/Spa plus" &&
          this.selectedPackages.map((e) => e.packageName).includes("Hydrojet")
        ) {
          this.selectedPackages = this.selectedPackages.filter((e) => e.packageName !== "Hydrojet")
        }
      } else {
        if (
          this.studio.code === "stgalleneinstein" &&
          pck.packageName === "Hydrojet" &&
          !(
            this.selectedPackages.map((e) => e.packageName).includes("Wellness/Spa plus") || this.possibilities.packages.includes("Wellness/Spa plus")
          )
        ) {
          alert("Nur mit Wellness/Spa plus")
        } else {
          if (pck.amountSelectable) {
            this.packageAmountSelect = pck
            this.packageAmountSelect.amount = 1
          }
          this.selectedPackages.push(pck)
        }
      }
    },
    displayCurrency(amount) {
      return roundToNearestFiveCents(amount)
    },

    async updateUserData() {
      if (!this.checkIfBirthdayIsValid(this.customer.infos.birthday)) {
        this.$toast.error("Bitte gib ein gültiges Geburtsdatum ein")
        return
      }
      this.updatingUserData = true
      try {
        let status = await api.editUserProfileFromContract({
          data: {
            infos: this.customer.infos,
            userId: this.customer.selection,
          },
        })

        if (!status.success && status.message) this.$toast.error(status.message)
        this.updatingUserData = false
        if (!status.success) return false
      } catch (e) {
        this.$toast.error("Fehler beim aktualisieren des Profils, bitte versuche es erneut")
        this.updatingUserData = false
        return false
      }
      this.updatingUserData = false
      this.fetchUpgradePossibilities()
    },
    async fetchUpgradePossibilities() {
      this.checkIfUnderage()
      this.selectedPackages = []
      this.step = 2

      this.fetchingPossibilities = true
      let customerId = this.customer.selection
      try {
        let local = this.userService.hasRight("c_create") && window.location.href.includes("local")
        let infos = await api.fetchPackageUpgradePossibilitesTemp(customerId, local)
        this.possibilities = infos
      } catch (e) {
        this.$toast.error("Fehler beim laden der Möglichkeiten")
        console.log(e)
      }
      this.fetchingPossibilities = false
    },
    logthis() {
      console.log(this.customer)
    },
    resetCustomer() {
      this.customer.infos = null
      this.resetLegalRepresentative()
      this.customer.isUnderage = false
      this.customer.selection = null
    },
    checkIfUnderage() {
      let birthday = this.customer.infos.birthday
      let age = this.$moment().diff(birthday, "years")

      if (age < 18) {
        this.customer.isUnderage = true
      } else {
        this.customer.isUnderage = false
      }

      this.$forceUpdate()
    },
    resetLegalRepresentative() {
      this.customer.legalRepresentative = {
        firstName: null,
        lastName: null,
        email: null,
        address: null,
        firstSignature: null,
        secondSignature: null,
      }
    },
    async updateCreator(id) {
      this.customer.contractCreator = id
      let contractCreator = await api.getUserForManager(id)
      let contractCreatorInfos = {
        firstName: contractCreator.vorname,
        lastName: contractCreator.nachname,
        email: contractCreator.email,
      }

      this.customer.contractCreatorInfos = contractCreatorInfos

      this.$forceUpdate()
    },
    checkIfBirthdayIsValid(birthday) {
      return this.$moment(birthday, "YYYY-MM-DD", true).isValid()
    },
    async updateCustomer(id) {
      this.customer.selection = id
      let customer = await api.getUserForManager(id)
      let customerEntry = {
        gender: customer.geschlecht,
        firstName: customer.vorname,
        lastName: customer.nachname,
        email: customer.email,
        phone: customer.telefon,
        birthday: customer.geburtstag,
        street: customer.adresse_1,
        city: customer.stadt,
        zip: customer.plz,
      }

      this.resetLegalRepresentative()

      this.customer.infos = customerEntry

      if (!this.checkIfBirthdayIsValid(this.customer.infos.birthday)) {
        this.$toast.error("Bitte gib ein gültiges Geburtsdatum ein")
      } else {
        this.checkIfUnderage()
      }

      this.$forceUpdate()
    },
  },
}
</script>

<style lang="scss" scoped>
// STYLES BLEIBEN IDENTISCH ZUM VORHERIGEN MAL
.package-card {
  position: relative;
  border-radius: 12px;
  border: 2px solid transparent;
  transition: all 0.2s ease-in-out;
  cursor: pointer;

  .package-title {
    font-weight: bold;
    line-height: 1.4;
  }

  .package-subtitle {
    font-size: 1rem;
    color: #4caf50;
    font-weight: 500;
  }

  .package-subtitle .text-decoration-line-through {
    color: #f44336;
    font-size: 0.8rem;
  }

  .package-card__check-icon {
    position: absolute;
    top: 12px;
    right: 12px;
    opacity: 0;
    transform: scale(0.8);
    transition: all 0.2s ease-in-out;
    z-index: 1;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1) !important;
  }

  &--selected {
    background: var(--v-primary-lighten-4);
    border: 2px solid var(--v-primary);
    color: var(--v-primary-darken-3);

    .package-card__check-icon {
      opacity: 1;
      transform: scale(1);
    }

    &::before {
      content: "";
      position: absolute;
      top: -2px;
      right: -2px;
      width: 48px;
      height: 48px;
      background: var(--v-primary);
      border-bottom-left-radius: 20px;
      border-top-right-radius: 10px;
      z-index: 0;
    }
  }
}

.payment-card {
  width: 150px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: 2px solid transparent;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1) !important;
  }

  &--selected {
    border: 2px solid var(--v-primary);
    background: var(--v-primary-lighten-5);
    color: var(--v-primary-darken-3);
  }
}

.special-offer-chip {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: var(--v-primary);
  color: white;
  font-size: 12px;
  font-weight: bold;
  width: auto;
  padding: 2px 10px;
  border-radius: 15px;
  text-align: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

::v-deep .v-card__title {
  position: sticky;
  top: 0;
  z-index: 2;
}

// BLACK FRIDAY STYLES
.black-friday-banner {
  background: linear-gradient(135deg, #111 0%, #333 100%);
  border-left: 5px solid var(--v-primary) !important;
  color: white;
  border-radius: 12px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  .v-icon {
    color: #ffd700 !important;
    animation: pulse 1.5s infinite;
  }
}

.black-friday-chip {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #111;
  color: #ffd700;
  font-size: 11px;
  font-weight: bold;
  padding: 4px 12px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  border: 1px solid #ffd700;
  z-index: 2;
  .v-icon {
    color: #ffd700 !important;
    font-size: 14px;
    margin-right: 4px;
  }
}

.bf-price-new {
  font-size: 1.1rem;
  font-weight: bold;
  color: #4caf50;
}
.bf-price-old {
  font-size: 0.9rem;
  color: #f44336;
}

.package-card--bf.package-card--selected {
  // background: #fff8e1;
  border: 2px solid #ffd700;

  &::before {
    background: #111;
  }
  .package-card__check-icon {
    color: #ffd700;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}
</style>
